import { ScannerError, normalizeError } from '../utils/errors.js';
import {
    inferFacingFromLabel,
    rankCameras,
} from './camera-selection.js';
import {
    buildConstraintAttempts,
    stopStream,
    waitForVideo,
} from './camera-stream.js';
import {
    describeTrack,
    mergeCameraProbe,
    normalizeTrackInfo,
} from './track-capabilities.js';
import {
    applyTorch,
    applyZoom,
    enableContinuousAutofocus,
} from './CameraFeatures.js';
import {
    buildAdaptiveVideoConstraints,
    createAdaptiveProfile,
} from './adaptive-profile.js';

export { inferFacingFromLabel, rankCameras } from './camera-selection.js';

function readSafely(callback, fallback = null) {
    try {
        return callback() ?? fallback;
    } catch {
        return fallback;
    }
}

function inspectTrack(track) {
    if (!track) {
        return null;
    }

    return {
        readyState: track.readyState || null,
        enabled: track.enabled ?? null,
        muted: track.muted ?? null,
        kind: track.kind || null,
        label: track.label || null,
        settings: readSafely(() => track.getSettings?.(), {}),
        capabilities: readSafely(() => track.getCapabilities?.(), {}),
    };
}

export class CameraManager {
    constructor({
        logger,
        navigatorLike = globalThis.navigator,
        windowLike = globalThis.window,
        onTrackEnded,
    } = {}) {
        this.logger = logger;
        this.navigator = navigatorLike;
        this.window = windowLike;
        this.onTrackEnded = onTrackEnded;
        this.cameras = [];
        this.stream = null;
        this.track = null;
        this.video = null;
        this.currentDeviceId = null;
        this.autofocusApplied = false;
        this.torchEnabled = false;
        this.pendingStream = null;
        this.pendingDescription = null;
        this.adaptiveProfile = createAdaptiveProfile({
            navigatorLike,
            windowLike,
        });
    }

    trace(step, event, message, details = {}) {
        this.logger?.info(
            `camera.trace.${String(step).padStart(2, '0')}_${event}`,
            `[${step}] ${message}`,
            details
        );
    }

    traceFailure(step, event, error, {
        deviceId = null,
        constraints = null,
        stream = this.stream,
        track = this.track,
        video = this.video,
    } = {}) {
        this.logger?.error(
            `camera.trace.${String(step).padStart(2, '0')}_${event}_failed`,
            `[${step}] Falha em ${event}.`,
            error,
            {
                originalError: {
                    name: error?.name || error?.constructor?.name || null,
                    message: error?.message || String(error),
                    stack: error?.stack || null,
                    constraint: error?.constraint || null,
                },
                constraint: error?.constraint || null,
                constraints,
                deviceId,
                stream: stream
                    ? {
                        active: stream.active ?? null,
                        id: stream.id || null,
                    }
                    : null,
                track: inspectTrack(track),
                video: video
                    ? {
                        readyState: video.readyState,
                        paused: video.paused,
                        ended: video.ended,
                        autoplay: video.autoplay,
                        muted: video.muted,
                        playsInline: video.playsInline,
                        hasSrcObject: Boolean(video.srcObject),
                    }
                    : null,
            }
        );
    }

    assertSupport() {
        const hostname = this.window?.location?.hostname;
        const localHost = ['localhost', '127.0.0.1', '::1']
            .includes(hostname);

        if (!this.window?.isSecureContext && !localHost) {
            throw new ScannerError(
                'SECURE_CONTEXT_REQUIRED',
                'HTTPS obrigatório para acessar a câmera neste endereço.',
                { context: 'camera' }
            );
        }

        if (!this.navigator?.mediaDevices?.getUserMedia) {
            throw new ScannerError(
                'CAMERA_API_UNAVAILABLE',
                'Camera API indisponível neste navegador.',
                { context: 'camera' }
            );
        }
    }

    async discover({
        requestPermission = false,
        probeCapabilities = false,
    } = {}) {
        this.assertSupport();

        const mediaDevices = this.navigator.mediaDevices;
        let preferredDeviceId = null;
        let permissionSample = null;

        if (requestPermission) {
            this.clearPendingStream();
            const permissionConstraints = {
                audio: false,
                video: buildAdaptiveVideoConstraints({
                    profile: this.adaptiveProfile,
                    facingMode: 'environment',
                }),
            };

            this.trace(
                1,
                'permission_request',
                'Solicitando permissão.',
                {
                    profile: this.adaptiveProfile,
                    constraints: permissionConstraints,
                }
            );

            try {
                const permissionStream = await mediaDevices.getUserMedia(
                    permissionConstraints
                );
                const track = permissionStream.getVideoTracks()[0];

                permissionSample = track
                    ? describeTrack(track)
                    : null;
                preferredDeviceId = permissionSample?.settings.deviceId || null;
                this.pendingStream = permissionStream;
                this.pendingDescription = permissionSample;

                this.trace(
                    2,
                    'permission_granted',
                    'Permissão concedida.',
                    {
                        stream: {
                            active: permissionStream.active ?? null,
                            id: permissionStream.id || null,
                        },
                        track: inspectTrack(track),
                    }
                );
            } catch (error) {
                const normalized = normalizeError(error, 'camera.permission');

                this.traceFailure(1, 'permission_request', error, {
                    constraints: permissionConstraints,
                });
                throw normalized;
            }
        }

        if (!mediaDevices.enumerateDevices) {
            this.cameras = [{
                index: 0,
                deviceId: '',
                groupId: '',
                label: 'Câmera padrão',
                labelAvailable: false,
                facingMode: (
                    permissionSample?.settings.facingMode
                    || 'unknown'
                ),
                width: Number(permissionSample?.settings.width) || null,
                height: Number(permissionSample?.settings.height) || null,
                maxWidth: null,
                maxHeight: null,
                maxPixels: 0,
                probeError: 'Enumeração indisponível',
                preferred: true,
            }];

            this.logger?.warn(
                'camera.discovery.unavailable',
                'Enumeração de câmeras indisponível; será usada a câmera padrão.'
            );

            return this.getCameras();
        }

        this.trace(3, 'enumerate_devices', 'Executando enumerateDevices().');
        let devices;

        try {
            devices = await mediaDevices.enumerateDevices();
            this.trace(
                3,
                'enumerate_devices_completed',
                'enumerateDevices() concluído.',
                {
                    total: devices.length,
                    videoInputs: devices.filter(
                        (device) => device.kind === 'videoinput'
                    ).map((device) => ({
                        deviceId: device.deviceId,
                        groupId: device.groupId,
                        label: device.label,
                    })),
                }
            );
        } catch (error) {
            this.traceFailure(3, 'enumerate_devices', error);
            throw normalizeError(error, 'camera.enumerate');
        }

        const videoDevices = devices.filter(
            (device) => device.kind === 'videoinput'
        );

        if (videoDevices.length === 0) {
            throw new ScannerError(
                'CAMERA_NOT_FOUND',
                'Nenhuma câmera foi encontrada neste dispositivo.',
                { context: 'camera.discovery' }
            );
        }

        const cameras = videoDevices.map((device, index) => ({
            index,
            deviceId: device.deviceId,
            groupId: device.groupId,
            label: device.label || `Câmera ${index + 1}`,
            labelAvailable: Boolean(device.label),
            facingMode: (
                device.deviceId
                && device.deviceId === preferredDeviceId
                && permissionSample?.settings.facingMode
            ) || inferFacingFromLabel(device.label),
            width: null,
            height: null,
            maxWidth: null,
            maxHeight: null,
            maxPixels: 0,
            probeError: null,
            preferred: Boolean(
                device.deviceId
                && device.deviceId === preferredDeviceId
            ),
        }));

        if (permissionSample && preferredDeviceId) {
            const preferred = cameras.find(
                (camera) => camera.deviceId === preferredDeviceId
            );

            if (preferred) {
                mergeCameraProbe(preferred, permissionSample);
            }
        }

        if (probeCapabilities) {
            for (const camera of cameras) {
                if (!camera.deviceId) {
                    continue;
                }

                try {
                    const probe = await this.probe(camera.deviceId);
                    mergeCameraProbe(camera, probe);
                } catch (error) {
                    const normalized = normalizeError(
                        error,
                        'camera.probe'
                    );

                    camera.probeError = normalized.message;
                    this.logger?.warn(
                        'camera.probe.failed',
                        `Não foi possível medir "${camera.label}".`,
                        {
                            deviceId: camera.deviceId,
                            reason: normalized.message,
                        }
                    );
                }
            }
        }

        this.cameras = rankCameras(cameras);
        this.adaptiveProfile = createAdaptiveProfile({
            navigatorLike: this.navigator,
            windowLike: this.window,
            camera: this.cameras[0],
        });
        this.trace(
            4,
            'profile_selected',
            'Perfil escolhido.',
            {
                camera: this.cameras[0] || null,
                profile: this.adaptiveProfile,
            }
        );

        this.logger?.info(
            'camera.discovery.completed',
            `${this.cameras.length} câmera(s) encontrada(s).`,
            {
                selected: this.cameras[0]?.label,
                cameras: this.cameras.map((camera) => ({
                    label: camera.label,
                    facingMode: camera.facingMode,
                    maxWidth: camera.maxWidth,
                    maxHeight: camera.maxHeight,
                })),
            }
        );

        return this.getCameras();
    }

    async probe(deviceId) {
        const stream = await this.navigator.mediaDevices.getUserMedia({
            audio: false,
            video: buildAdaptiveVideoConstraints({
                profile: this.adaptiveProfile,
                deviceId,
            }),
        });

        try {
            const track = stream.getVideoTracks()[0];

            if (!track) {
                throw new ScannerError(
                    'CAMERA_VIDEO_TRACK_MISSING',
                    'A câmera não forneceu uma faixa de vídeo.',
                    { recoverable: true, context: 'camera.probe' }
                );
            }

            return describeTrack(track);
        } finally {
            stopStream(stream);
        }
    }

    async open(video, deviceId = null) {
        this.assertSupport();
        this.stop({ preservePendingStream: true });

        if (!video) {
            throw new ScannerError(
                'VIDEO_ELEMENT_MISSING',
                'Elemento de vídeo do scanner não foi encontrado.',
                { context: 'camera' }
            );
        }

        const selectedCamera = (
            this.cameras.find((camera) => camera.deviceId === deviceId)
            || this.cameras[0]
            || null
        );
        this.adaptiveProfile = createAdaptiveProfile({
            navigatorLike: this.navigator,
            windowLike: this.window,
            camera: selectedCamera,
        });
        const attempts = buildConstraintAttempts(
            deviceId,
            this.adaptiveProfile
        );
        let lastError = null;
        const pendingStream = this.takePendingStream(deviceId);

        if (pendingStream) {
            this.trace(
                5,
                'final_constraints',
                'Stream de permissão compatível; nova constraint não é necessária.',
                {
                    deviceId,
                    source: 'permission-stream',
                    profile: this.adaptiveProfile,
                }
            );
            this.trace(
                6,
                'get_user_media',
                'getUserMedia já concluído na etapa de permissão; reutilizando stream.'
            );
            this.trace(
                7,
                'stream_received',
                'Stream recebida e selecionada para reuso.',
                {
                    stream: {
                        active: pendingStream.active ?? null,
                        id: pendingStream.id || null,
                    },
                    track: inspectTrack(
                        pendingStream.getVideoTracks?.()[0]
                    ),
                }
            );

            try {
                return await this.attachStream({
                    video,
                    stream: pendingStream,
                    deviceId,
                    source: 'stream de permissão reutilizado',
                });
            } catch (error) {
                lastError = normalizeError(error, 'camera.open');
                this.traceFailure(7, 'warm_stream', error, {
                    deviceId,
                    stream: pendingStream,
                    track: pendingStream.getVideoTracks?.()[0],
                    video,
                });
                this.logger?.warn(
                    'camera.open.warm_stream_failed',
                    lastError.message,
                    { code: lastError.code }
                );
                this.stop();
            }
        }

        for (const attempt of attempts) {
            let stream = null;

            try {
                this.trace(
                    5,
                    'final_constraints',
                    'Constraints finais.',
                    {
                        attempt: attempt.label,
                        deviceId,
                        constraints: attempt.constraints,
                        profile: this.adaptiveProfile,
                    }
                );
                this.trace(
                    6,
                    'get_user_media',
                    'getUserMedia iniciado.',
                    {
                        attempt: attempt.label,
                        constraints: attempt.constraints,
                    }
                );

                stream = await this.navigator.mediaDevices.getUserMedia(
                    attempt.constraints
                );
                this.trace(
                    7,
                    'stream_received',
                    'Stream recebida.',
                    {
                        attempt: attempt.label,
                        stream: {
                            active: stream.active ?? null,
                            id: stream.id || null,
                        },
                        track: inspectTrack(stream.getVideoTracks?.()[0]),
                    }
                );
                return await this.attachStream({
                    video,
                    stream,
                    deviceId,
                    source: attempt.label,
                });
            } catch (error) {
                if (!stream) {
                    this.traceFailure(6, 'get_user_media', error, {
                        deviceId,
                        constraints: attempt.constraints,
                        video,
                    });
                }
                lastError = normalizeError(error, 'camera.open');

                this.logger?.warn(
                    'camera.open.attempt_failed',
                    `${attempt.label}: ${lastError.message}`,
                    { code: lastError.code }
                );

                this.stop();

                if (!lastError.recoverable) {
                    throw lastError;
                }
            }
        }

        throw lastError || new ScannerError(
            'CAMERA_OPEN_FAILED',
            'Não foi possível abrir nenhuma câmera disponível.',
            { recoverable: true, context: 'camera.open' }
        );
    }

    async attachStream({ video, stream, deviceId, source }) {
        const track = stream.getVideoTracks()[0];

        if (!track) {
            stopStream(stream);
            throw new ScannerError(
                'CAMERA_VIDEO_TRACK_MISSING',
                'A câmera abriu sem fornecer uma faixa de vídeo.',
                { recoverable: true, context: 'camera.open' }
            );
        }

        this.trace(
            8,
            'track_created',
            'Track criada.',
            { source, deviceId, track: inspectTrack(track) }
        );
        this.trace(
            9,
            'track_state',
            'Estado da track capturado.',
            inspectTrack(track)
        );

        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.muted = true;
        video.autoplay = true;
        this.stream = stream;
        this.track = track;
        this.video = video;
        this.currentDeviceId = (
            track.getSettings?.().deviceId
            || deviceId
            || null
        );
        this.autofocusApplied = false;
        this.torchEnabled = false;

        track.addEventListener?.('ended', () => {
            if (this.track === track) {
                this.onTrackEnded?.();
            }
        });

        video.srcObject = stream;
        this.trace(
            10,
            'video_src_object',
            'Video recebeu srcObject.',
            {
                readyState: video.readyState,
                paused: video.paused,
                track: inspectTrack(track),
            }
        );

        let stage = 11;

        try {
            const ready = await waitForVideo(video);

            this.trace(
                11,
                'loaded_metadata',
                'Video recebeu metadata.',
                {
                    event: ready.event,
                    readyState: ready.readyState,
                    videoWidth: video.videoWidth || null,
                    videoHeight: video.videoHeight || null,
                }
            );
            stage = 12;
            this.trace(
                12,
                'video_play',
                'Executando video.play().',
                {
                    readyState: video.readyState,
                    paused: video.paused,
                    autoplay: video.autoplay,
                    muted: video.muted,
                    playsInline: video.playsInline,
                }
            );
            await video.play();
            stage = 13;
            this.trace(
                13,
                'video_playing',
                'Video em playing.',
                {
                    readyState: video.readyState,
                    paused: video.paused,
                    ended: video.ended,
                    videoWidth: video.videoWidth || null,
                    videoHeight: video.videoHeight || null,
                    track: inspectTrack(track),
                }
            );
        } catch (error) {
            this.traceFailure(
                stage,
                stage === 11 ? 'loaded_metadata' : 'video_play',
                error,
                { deviceId, stream, track, video }
            );
            throw error;
        }

        const info = this.getCurrentInfo();

        this.logger?.info(
            'camera.opened',
            `Câmera aberta: ${info.label}.`,
            {
                source,
                deviceId: info.settings.deviceId,
                resolution: `${info.settings.width || '?'}x${info.settings.height || '?'}`,
                frameRate: info.settings.frameRate,
                facingMode: info.settings.facingMode,
                adaptiveProfile: this.adaptiveProfile,
            }
        );

        return info;
    }

    getCurrentInfo() {
        if (!this.track) {
            return null;
        }

        return normalizeTrackInfo({
            track: this.track,
            currentDeviceId: this.currentDeviceId,
            autofocusApplied: this.autofocusApplied,
            torchEnabled: this.torchEnabled,
        });
    }

    async enableContinuousAutofocus() {
        const result = await enableContinuousAutofocus({
            track: this.track,
            cameraInfo: this.getCurrentInfo(),
            logger: this.logger,
        });

        this.autofocusApplied = result.applied;

        return result;
    }

    async setTorch(enabled) {
        await applyTorch({
            track: this.track,
            cameraInfo: this.getCurrentInfo(),
            enabled,
            logger: this.logger,
        });
        this.torchEnabled = Boolean(enabled);

        return this.getCurrentInfo();
    }

    async setZoom(value) {
        await applyZoom({
            track: this.track,
            cameraInfo: this.getCurrentInfo(),
            value,
            logger: this.logger,
        });

        return this.getCurrentInfo();
    }

    getCameras() {
        return this.cameras.map((camera) => ({ ...camera }));
    }

    getAdaptiveProfile() {
        return typeof structuredClone === 'function'
            ? structuredClone(this.adaptiveProfile)
            : JSON.parse(JSON.stringify(this.adaptiveProfile));
    }

    takePendingStream(deviceId) {
        const stream = this.pendingStream;
        const description = this.pendingDescription;

        this.pendingStream = null;
        this.pendingDescription = null;

        if (!stream) {
            return null;
        }

        const track = stream.getVideoTracks?.()[0];
        const pendingDeviceId = description?.settings?.deviceId;
        const compatible = (
            track?.readyState !== 'ended'
            && (
                !deviceId
                || !pendingDeviceId
                || deviceId === pendingDeviceId
            )
        );

        if (!compatible) {
            stopStream(stream);
            return null;
        }

        return stream;
    }

    clearPendingStream() {
        if (this.pendingStream) {
            stopStream(this.pendingStream);
        }

        this.pendingStream = null;
        this.pendingDescription = null;
    }

    stop({ preservePendingStream = false } = {}) {
        if (this.stream) {
            stopStream(this.stream);
        }

        if (this.video?.srcObject) {
            this.video.srcObject = null;
        }

        this.stream = null;
        this.track = null;
        this.video = null;
        this.currentDeviceId = null;
        this.autofocusApplied = false;
        this.torchEnabled = false;

        if (!preservePendingStream) {
            this.clearPendingStream();
        }
    }
}
